import { UnknownMethodError } from '../../errors'
import type { ExecutionContext } from '../context/execution-context'
import type { Extension } from '../extension'
import type { LoaderOwner } from '../loaders/loader-owner'
import { ExtensionMethod } from '../method'
import type { SharedExtensionMethod } from '../model/model-types'
import { toArgs, toBodyParams } from '../model/params-model'
import { encodeParams, MethodParams } from '../params'
import { getPath } from '../path'
import { ResultSpec } from '../result'
import type { ProviderPayload } from '../types'
import type { RealtimeTriggerProps } from './types'

// A realtime (webhook) trigger (09-realtime-triggers.md). Like a polling trigger it is a published method
// the console renders as a start node, but is NOT invocable by name — the runtime reaches it through the
// realtime SYSTEM seam (see ./realtime-setup.ts), which routes inbound deliveries to it.
//
// Its three author hooks answer three different questions, at three different moments:
//   providerEventNames  which provider events is this trigger about?      (routing, per delivery)
//   matches             does THIS configured instance care about it?      (fan-out, per instance)
//   extendResult        what extra data should the event carry?           (once, if anything matched)

export class RealtimeTriggerMethod extends ExtensionMethod implements LoaderOwner {
  readonly params: MethodParams
  readonly result: ResultSpec

  constructor(extension: Extension, private readonly props: RealtimeTriggerProps) {
    super(extension)

    this.params = MethodParams.of(props.params, {
      serviceId: extension.id,
      kind: 'realtime trigger',
      itemId: props.id,
      slot: 'params',
    })
    this.result = ResultSpec.from(props.result)
  }

  get id(): string {
    return this.props.id
  }

  get name(): string {
    return this.props.id
  }

  get registerAs(): string {
    return 'REALTIME_TRIGGER'
  }

  get category(): string {
    return this.props.category
  }

  get oauth2Scopes(): string[] {
    return this.props.oauth2Scopes ?? []
  }

  // Not dispatchable by name — inbound deliveries arrive through the realtime SYSTEM methods.
  execute(): never {
    throw new UnknownMethodError(this.extension.id, this.name)
  }

  // ── routing ────────────────────────────────────────────────────────────────

  // The provider event(s) this trigger claims. The fn form depends on instance params (unusual for routing)
  // — best-effort with the params given.
  providerEventNames(params: Record<string, unknown> = {}): string[] {
    const declared = this.props.providerEventName

    if (typeof declared === 'function') {
      return declared({ params }) ?? []
    }

    if (Array.isArray(declared)) {
      return declared
    }

    return declared ? [declared] : []
  }

  claims(providerEventName: string): boolean {
    return this.providerEventNames().includes(providerEventName)
  }

  // Per-trigger enrichment of the shaped event data, before instances are matched against it.
  extendEventData(requestBody: ProviderPayload, providerEventData: ProviderPayload): Record<string, unknown> | undefined {
    return this.props.extendEventData?.({ requestBody, providerEventData })
  }

  /**
   * Whether one CONFIGURED INSTANCE of this trigger cares about an event. Declarative form: type-coerced
   * equality per filled param, where an empty param means "any" — that is what lets a user leave a filter
   * blank and receive everything. Predicate form: the author decides. No `match` at all: always fires.
   */
  matches(params: Record<string, unknown>, providerEventData: ProviderPayload): boolean {
    const match = this.props.match

    if (!match) {
      return true
    }

    if (typeof match === 'function') {
      return !!match({ params, providerEventData })
    }

    for (const [paramKey, path] of Object.entries(match)) {
      const paramValue = params[paramKey]

      if (paramValue == null || paramValue === '') {
        continue
      }

      if (`${ paramValue }` !== `${ getPath(providerEventData, path) }`) {
        return false
      }
    }

    return true
  }

  // Runs ONCE per event if any instance matched — the only per-event place that may call the provider API.
  extendResult(providerEventData: ProviderPayload, ctx: ExecutionContext): Promise<unknown> | unknown {
    return this.props.extendResult?.(ctx.bagWith({ providerEventData }))
  }

  hasExtendResult(): boolean {
    return typeof this.props.extendResult === 'function'
  }

  // ── output model ───────────────────────────────────────────────────────────

  toModel(): SharedExtensionMethod {
    const [ownStart, ownEnd] = this.props.appearanceColors ?? []
    const [colorStart, colorEnd] = this.extension.appearanceColors

    return {
      name: this.id,
      operationName: this.props.label,
      description: this.props.description || '',
      registerAs: this.registerAs,
      category: this.category,
      colorStart: ownStart ?? colorStart,
      colorEnd: ownEnd ?? colorEnd,
      customIcon: this.props.logo ?? '',
      returnType: this.result.returnType,
      queryParams: [],
      bodyParams: toBodyParams(this.params),
      paramsSchema: encodeParams(this.params.scheme),
      metaInfo: { args: toArgs(this.params), ...this.result.toMetaInfo(this.id, this.params) },
    }
  }
}
